import React, { useState, useCallback } from 'react';
import { Upload, Mic } from 'lucide-react';
import { MediaUploader } from '@/components/common/MediaUploader';
import { AudioRecorder } from './AudioRecorder';
import { useTranslation } from '@/hooks/useTranslation';

type InputMode = 'upload' | 'record';

interface MediaInputProps {
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  disabled?: boolean;
  isUploading?: boolean;
  uploadProgress?: number;
  className?: string;
}

/**
 * MediaInput - Compositor component for media input
 * Provides tabs to switch between file upload and microphone recording
 */
export const MediaInput: React.FC<MediaInputProps> = ({
  onFileSelect,
  onRemove,
  disabled = false,
  isUploading = false,
  uploadProgress = 0,
  className = '',
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<InputMode>('upload');
  const [hasRecording, setHasRecording] = useState(false);

  const handleRecordingComplete = useCallback(
    (file: File) => {
      setHasRecording(true);
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleRemove = useCallback(() => {
    setHasRecording(false);
    onRemove?.();
  }, [onRemove]);

  const handleModeChange = useCallback(
    (newMode: InputMode) => {
      if (disabled || isUploading) return;
      setMode(newMode);
      // Clear state when switching modes
      setHasRecording(false);
      onRemove?.();
    },
    [disabled, isUploading, onRemove]
  );

  return (
    <div className={`w-full ${className}`}>
      {/* Mode Tabs */}
      <div className="flex border-b border-white/10 mb-4">
        <button
          onClick={() => handleModeChange('upload')}
          disabled={disabled || isUploading}
          className={`
            flex items-center space-x-2 px-4 py-3 font-medium text-sm
            border-b-2 transition-colors
            ${
              mode === 'upload'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-white/50 hover:text-white/70 hover:border-white/20'
            }
            ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <Upload className="w-4 h-4" />
          <span>
            {t('stt.mediaInput.uploadTab', {
              defaultValue: 'Upload File',
            })}
          </span>
        </button>

        <button
          onClick={() => handleModeChange('record')}
          disabled={disabled || isUploading}
          className={`
            flex items-center space-x-2 px-4 py-3 font-medium text-sm
            border-b-2 transition-colors
            ${
              mode === 'record'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-white/50 hover:text-white/70 hover:border-white/20'
            }
            ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <Mic className="w-4 h-4" />
          <span>
            {t('stt.mediaInput.recordTab', {
              defaultValue: 'Record Audio',
            })}
          </span>
        </button>
      </div>

      {/* Content based on mode */}
      {mode === 'upload' ? (
        <MediaUploader
          accept={['video', 'audio']}
          onFileSelect={(file) => handleFileSelect(file)}
          onRemove={handleRemove}
          disabled={disabled}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          translationNamespace="stt"
        />
      ) : (
        <AudioRecorder
          onRecordingComplete={handleRecordingComplete}
          disabled={disabled || isUploading}
        />
      )}

      {/* Recording selected indicator */}
      {mode === 'record' && hasRecording && !isUploading && (
        <div className="mt-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-sm text-green-300">
            {t('stt.mediaInput.recordingSelected', {
              defaultValue: 'Recording ready. Click "Start Transcription" to process.',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
